import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../hooks/useCurrency';
import type { PromotionDiscount } from '../../api/types';
import './PromoPrice.scss';

interface PromoPriceProps {
  original: number | string | undefined;
  effective?: number | string;
  promotion?: PromotionDiscount & { name?: string };
  /**
   * Overrides the computed badge text ("−30%" / "−<amount>") with a literal
   * string. For call sites that only have a frozen order line item — no
   * `amount`/`percent` to recompute from, just whatever text was snapshotted
   * at order time. An empty string is treated as "not provided" and falls
   * back to the computed badge — pass a non-empty label to override.
   */
  badgeLabel?: string;
  /**
   * Overrides the account-currency formatter from `useCurrency()`. Pass this
   * when the entity carries its own `currency_code` (an order, a frozen line
   * item) — per FRONTEND-GUIDELINES, historical rows must render in the
   * currency they were created in, not whatever the viewer's account
   * currently uses.
   */
  format?: (value: number | string | null | undefined) => string;
}

// Sale price treatment used everywhere prices render (see FRONTEND-GUIDELINES).
// Without `promotion` it renders the plain formatted price, so it is a safe
// drop-in replacement for `format(price)` call sites.
const PromoPrice = ({ original, effective, promotion, badgeLabel, format: formatOverride }: PromoPriceProps) => {
  const { format: accountFormat } = useCurrency();
  const format = formatOverride ?? accountFormat;
  const { t } = useTranslation();
  if (!promotion || effective === undefined) return <span className="promo-price">{format(original)}</span>;

  const badge = badgeLabel || (
    promotion.kind === 'multiplicative'
      ? `−${Number(promotion.percent)}%`
      : `−${format(promotion.amount)}`
  );

  return (
    <span className="promo-price">
      <span className="promo-price-original" data-testid="promo-original">
        <span className="is-sr-only">{t('promo.original_price')} </span>
        {format(original)}
      </span>
      <span className="promo-price-effective has-text-danger" data-testid="promo-effective">
        <span className="is-sr-only">{t('promo.sale_price')} </span>
        {format(effective)}
      </span>
      <span className="tag is-danger promo-price-badge" data-testid="promo-badge" title={promotion.name}>{badge}</span>
    </span>
  );
};

export default PromoPrice;
